import { UiSchema } from '@rjsf/utils';

import { FileType } from '@/features/shared/const/fileTypes';
import { getMimeTypesForFiles } from '@/features/shared/utils/getMimeTypesForFiles';
import { buildMaskedDateOptions } from '@/utils/maskedDateOptions';
import { TFunction } from 'i18next';

const maskedDateOptionsBirthday = buildMaskedDateOptions();
const maskedDateOptionsImplementationPeriod = buildMaskedDateOptions(2100);

export const getUiSchema = (t: TFunction): UiSchema =>  {
	return {
		props: {
			className: 'flex flex-col gap-y-6',
		},
		stage: {
			'ui:widget': 'textarea',
		},
		title: {
			'ui:widget': 'textarea',
		},
		description: {
			'ui:widget': 'textarea',
		},
		problem: {
			// 'ui:widget': 'textarea',
		},
		product: {
			'ui:widget': 'textarea',
		},
		auditory: {
			// 'ui:widget': 'textarea',
		},
		phone: {
			'ui:widget': 'masked',
			'ui:options': {
				mask: '+7 (000) 000-00-00',
			},
			'ui:placeholder': '+7 (999) 999-99-99',
		},
		leaderPhone: {
			'ui:widget': 'masked',
			'ui:options': {
				mask: '+7 (000) 000-00-00',
			},
			'ui:placeholder': '+7 (999) 999-99-99',
		},
		managerPhone: {
			'ui:widget': 'masked',
			'ui:options': {
				mask: '+7 (000) 000-00-00',
			},
			'ui:placeholder': '+7 (999) 999-99-99',
		},
		websites: {
			items: {
				'ui:widget': 'URLWidget',
				'ui:placeholder': 'https://',
			},
		},
		videoLinks: {
			items: {
				'ui:widget': 'URLWidget',
				'ui:placeholder': 'https://',
			},
		},
        organizationWebsite: {
            'ui:widget': 'URLWidget',
            'ui:placeholder': 'https://',
        },
		leaderSocials: {
			items: {
				'ui:widget': 'URLWidget',
				'ui:placeholder': 'https://',
			},
		},
		implementationCountries: {
			items: {
				'ui:widget': 'country',
				'ui:placeholder': t('common.selectList'),
			},
		},
		applicantCountry: {
			'ui:widget': 'country',
		},
		currentRegions: {
			items: {
				'ui:placeholder': t('common.selectList'),
			},
		},
		presentation: {
			'ui:widget': 'file',
			'ui:options': {
				accept: getMimeTypesForFiles(FileType.PDF, FileType.POWERPOINT, FileType.WORD, FileType.EXCEL).join(','),
				size: 31457280,
			},
		},
		budget: {
			'ui:widget': 'file',
			'ui:options': {
				accept: getMimeTypesForFiles(FileType.TEXT, FileType.POWERPOINT, FileType.WORD, FileType.EXCEL).join(','),
				size: 15728640,
			},
		},
		cardCover: {
			'ui:widget': 'file',
		},
		businessPlan: {
			'ui:widget': 'file',
			'ui:options': {
				accept: getMimeTypesForFiles(FileType.WORD, FileType.PDF, FileType.EXCEL).join(','),
				size: 15728640,
			},
		},
		awards: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.TEXT, FileType.WORD, FileType.PDF, FileType.EXCEL).join(','),
			},
		},
		feedbackFiles: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.WORD).join(','),
				size: 15728640,
			},
		},
		subjectApplicationForParticipantInCoolCountry: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.WORD, FileType.RAR, FileType.ZIP).join(','),
			},
		},
		subjectApplicationProcessInfo: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		infoAboutDevelopTourism: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		listOfEducationOrganisations: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		applicationForInclusionInCoolCountry: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		parentConsentToParticipate: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		tourOperatorsList: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		displayObjectsList: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		representativesList: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		specialistsList: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		consentToProcessingPersonalData: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		consentToProcessingAndDistributionPhoto: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		teamCreationDeclaration: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.PDF, FileType.ZIP, FileType.RAR, FileType.WORD).join(','),
			},
		},
		coFinancing: {
			'ui:placeholder': '100 000',
			'ui:widget': 'masked',
			'ui:options': {
				// thousandsSeparator: ' ',
				mask: Number,
				min: 0,
				max: 92233720368547, // FIXME: Get values from Schema
			},
		},
		requestedAmount: {
			'ui:placeholder': '100 000',
			'ui:widget': 'masked',
			'ui:options': {
				// thousandsSeparator: ' ', TODO: Resolve value error
				mask: Number,
				min: 0,
				max: 92233720368547, // FIXME: Get values from Schema
			},
		},
		implementationPeriod: {
			'ui:widget': 'masked',
			'ui:options': maskedDateOptionsImplementationPeriod,
			'ui:placeholder': t('form.dateFormat'),
		},
		applicantBirthday: {
			'ui:widget': 'masked',
			'ui:options': maskedDateOptionsBirthday,
			'ui:placeholder': t('form.dateFormat'),
		},
		nationalInclusiveAgreement: {
			'ui:widget': 'file',
			'ui:options': {
				accept: getMimeTypesForFiles(FileType.PDF),
				size: 15728640,
			},
		},
		applicantINN: {
			'ui:widget': 'masked',
			'ui:options': {
				mask: '000000000000',
			},
			'ui:placeholder': 'XXXXXXXXXXXX',
		},
		orgINN: {
			'ui:widget': 'masked',
			'ui:options': {
				mask: [
					{
						mask: '000000000000',
					},
					{
						mask: '0000000000',
					},
				],
			},
			'ui:placeholder': 'XXXXXXXXXX',
		},
		orgOGRN: {
			'ui:widget': 'masked',
			'ui:options': {
				mask: '000000000000000',
			},
			'ui:placeholder': 'XXXXXXXXXXXXX',
		},
		subDirection: {
			'ui:widget': 'subDirection',
		},
		subSubDirection: {
			'ui:widget': 'subSubDirection',
		},
		finansModel: {
			'ui:widget': 'file',
			'ui:options': {
				accept: getMimeTypesForFiles(FileType.WORD, FileType.PDF, FileType.EXCEL).join(','),
				size: 15728640,
			},
		},
		otchetnost: {
			'ui:widget': 'file',
			'ui:options': {
				accept: getMimeTypesForFiles(FileType.WORD, FileType.PDF, FileType.EXCEL).join(','),
				size: 15728640,
			},
		},
		leaderResume: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.WORD, FileType.PDF).join(','),
				size: 15728640,
			},
		},
		leaderPhoto: {
			'ui:widget': 'file',
			'ui:options': {
				accept: getMimeTypesForFiles(FileType.JPEG, FileType.TIFF, FileType.PNG).join(','),
				size: 15728640,
			},
		},
		exportProposalFile: {
			'ui:widget': 'file',
		},
		asiGoals: {
			'ui:label': false,
		},
		instructionsTemplates: { 'ui:widget': 'templates' },
		userUploadedFiles: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
				accept: getMimeTypesForFiles(FileType.WORD, FileType.PDF, FileType.EXCEL).join(','),
				size: 5242880,
			},
		},
		presentations: {
			'ui:widget': 'file',
			'ui:options': {
				multiple: true,
			},
		},
        nationalTargets: {
            items: {
                'ui:widget': 'nationalTargets',
                'ui:placeholder': t('form.selectNationalTargetsValue'),
                'ui:placeholderBase': t('form.selectNationalTargetsBaseValue'),
            }
        },
        nationalProjects: {
            items: {
                'ui:widget': 'nationalProjects',
                'ui:placeholder': t('form.selectNationalProjectsValue'),
                'ui:placeholderBase': t('form.selectNationalProjectsBaseValue'),
            }
        },
        potential: {
            'ui:widget': 'textarea',
        },
        marketAnalysis: {
            'ui:widget': 'textarea',
        },
	};
}
